/**
 * 免费翻译服务
 * 优先使用后端翻译API，备选使用 Google Translate 免费 API
 * 支持的语言：中文、英文、俄语、法语、乌克兰语、西班牙语、葡萄牙语、日语、韩语、阿拉伯语、乌兹别克语
 */

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// 语言代码映射
export const LANGUAGE_CODES = {
  zh: 'zh-CN',  // 中文
  en: 'en',     // 英文
  ru: 'ru',     // 俄语
  fr: 'fr',     // 法语
  uk: 'uk',     // 乌克兰语
  es: 'es',     // 西班牙语
  pt: 'pt',     // 葡萄牙语
  ja: 'ja',     // 日语
  ko: 'ko',     // 韩语
  ar: 'ar',     // 阿拉伯语
  uz: 'uz',     // 乌兹别克语
};

// 翻译缓存（避免重复请求）
const translationCache = new Map();

/**
 * 生成缓存键
 */
const getCacheKey = (text, targetLang) => `${targetLang}:${text}`;

/**
 * 使用后端API翻译文本
 */
const translateViaBackend = async (text, targetLang) => {
  try {
    const response = await fetch(`${API}/translate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, target_language: targetLang }),
    });
    const data = await response.json();
    return data.translated_text || text;
  } catch (error) {
    console.error('Backend translation error:', error);
    return null; // 返回null表示需要使用备选方案
  }
};

/**
 * 使用 Google Translate 免费 API 翻译文本 (备选)
 */
const translateViaGoogle = async (text, targetLang, sourceLang = 'auto') => {
  try {
    const targetCode = LANGUAGE_CODES[targetLang] || targetLang;
    const sourceCode = sourceLang === 'auto' ? 'auto' : (LANGUAGE_CODES[sourceLang] || sourceLang);
    
    const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sourceCode}&tl=${targetCode}&dt=t&q=${encodeURIComponent(text)}`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    let translatedText = '';
    if (data && data[0]) {
      for (const item of data[0]) {
        if (item[0]) {
          translatedText += item[0];
        }
      }
    }
    
    return translatedText || text;
  } catch (error) {
    console.error('Google translation error:', error);
    return text;
  }
};

/**
 * 翻译文本
 * @param {string} text - 要翻译的文本
 * @param {string} targetLang - 目标语言代码 (zh, en, ru, fr, uk, es, pt, ja, ko, ar, uz)
 * @param {string} sourceLang - 源语言代码，默认自动检测
 * @returns {Promise<string>} 翻译后的文本
 */
export const translateText = async (text, targetLang, sourceLang = 'auto') => {
  // 空文本直接返回
  if (!text || text.trim() === '') return text;
  
  // 如果目标语言是中文或英文，直接返回
  if (targetLang === 'zh' || targetLang === 'en') {
    return text;
  }
  
  // 检查缓存
  const cacheKey = getCacheKey(text, targetLang);
  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey);
  }
  
  // 优先使用后端API
  let translatedText = await translateViaBackend(text, targetLang);
  
  // 如果后端失败，使用Google备选
  if (!translatedText || translatedText === text) {
    translatedText = await translateViaGoogle(text, targetLang, sourceLang);
  }
  
  // 缓存结果
  if (translatedText && translatedText !== text) {
    translationCache.set(cacheKey, translatedText);
  }
  
  return translatedText || text;
};

/**
 * 批量翻译多个文本
 * @param {Object} texts - 要翻译的文本对象 { key1: text1, key2: text2 }
 * @param {string} targetLang - 目标语言代码
 * @returns {Promise<Object>} 翻译后的文本对象
 */
export const translateBatch = async (texts, targetLang) => {
  const results = {};
  const promises = [];
  
  for (const [key, text] of Object.entries(texts)) {
    if (text) {
      promises.push(
        translateText(text, targetLang).then(translated => {
          results[key] = translated;
        })
      );
    } else {
      results[key] = text;
    }
  }
  
  await Promise.all(promises);
  return results;
};

/**
 * 清除翻译缓存
 */
export const clearTranslationCache = () => {
  translationCache.clear();
};

/**
 * 获取本地化字段（优先使用对应语言字段，否则自动翻译）
 * @param {Object} item - 包含多语言字段的对象
 * @param {string} fieldName - 字段名（如 'title', 'description'）
 * @param {string} currentLang - 当前语言
 * @returns {Promise<string>} 本地化后的文本
 */
export const getLocalizedFieldWithTranslation = async (item, fieldName, currentLang) => {
  if (!item) return '';
  
  // 如果是中文或英文，直接返回对应字段
  if (currentLang === 'zh') {
    return item[fieldName] || item[`${fieldName}_en`] || '';
  }
  if (currentLang === 'en') {
    return item[`${fieldName}_en`] || item[fieldName] || '';
  }
  
  // 其他语言：优先使用英文版本进行翻译（英文翻译效果通常更好）
  const sourceText = item[`${fieldName}_en`] || item[fieldName] || '';
  if (!sourceText) return '';
  
  return await translateText(sourceText, currentLang, 'en');
};

export default {
  translateText,
  translateBatch,
  clearTranslationCache,
  getLocalizedFieldWithTranslation,
  LANGUAGE_CODES,
};
